/**
 * ACADS — the Kolkata tuition business, from September 2026 on.
 *
 * What is left here is the month dashboard for the HQ view (every teacher row
 * opens that teacher's collection sheet) and the styles the ACADS pages share.
 * The monthly entry grid and the student list that used to live here are gone:
 * collecting, correcting a payment and editing a student all happen on the
 * collect screens (AcadsCollect).
 */
import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import React, { useEffect, useState } from 'react'

import { fetchAcadsDispatch, fetchAcadsEntry, fetchAcadsSummary } from 'api'
import constants from 'app/constants'
import { showToast } from 'app/helpers'

const useStyles = makeStyles((theme) => ({
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    flexWrap: 'wrap',
    marginBottom: theme.spacing(1.5)
  },
  grow: { flexGrow: 1 },
  muted: { color: theme.palette.text.secondary },

  scroll: { overflow: 'auto', maxHeight: 'calc(100vh - 260px)' },
  table: {
    borderCollapse: 'separate',
    borderSpacing: 0,
    width: 'max-content',
    minWidth: '100%'
  },
  cell: {
    whiteSpace: 'nowrap',
    padding: '3px 6px',
    borderBottom: '1px solid #ececec'
  },
  head: {
    position: 'sticky',
    top: 0,
    zIndex: 3,
    height: 30,
    background: '#f4f6f8',
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: '0.03em',
    color: '#555'
  },
  filterHead: {
    position: 'sticky',
    top: 30,
    zIndex: 3,
    height: 34,
    background: '#f4f6f8',
    padding: '2px 4px',
    borderBottom: '2px solid #cfd6de'
  },
  froze: { position: 'sticky', zIndex: 2, background: '#fbfbfc' },
  frozeHead: { background: '#f4f6f8' },
  // The frozen heading cells stick both ways, so they must sit above the
  // frozen body cells (2) and the scrolling heading cells (3) — otherwise the
  // rows slide over the heading of the frozen columns.
  corner: { zIndex: 4 },
  edge: { borderRight: '2px solid #cfd6de' },
  dirty: { background: '#fffbe6' },
  num: { textAlign: 'right' },
  cellInput: {
    '& input': { padding: '3px 4px', fontSize: 13 },
    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
    '&:hover .MuiOutlinedInput-notchedOutline': { border: '1px solid #ccd' },
    '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
      border: '1px solid #1a5fd0'
    }
  },
  filterInput: { '& input': { padding: '4px 6px', fontSize: 12 } },

  // Four tiles, not ten. Each answers one question and carries its own
  // supporting line, so the detail is next to the number it explains instead
  // of being another tile competing with it.
  tiles: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  tile: {
    border: '1px solid #e4e7ec',
    borderRadius: 10,
    padding: theme.spacing(2, 2.25)
  },
  tileKey: {
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    color: '#666'
  },
  tileVal: {
    fontSize: 30,
    fontWeight: 600,
    marginTop: 4,
    lineHeight: 1.1,
    fontVariantNumeric: 'tabular-nums'
  },
  tileNote: {
    fontSize: 12.5,
    color: '#5a6169',
    marginTop: 6,
    lineHeight: 1.45
  },
  warn: { color: '#a4501a' },
  good: { color: '#14532d' },
  sectionTitle: {
    fontSize: 12,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: '#444',
    margin: theme.spacing(2.5, 0, 1)
  },
  subTabs: {
    borderBottom: '1px solid #e4e7ec',
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    minHeight: 38
  },
  clickRow: { cursor: 'pointer', '&:hover': { background: '#f0f6ff' } },
  picked: { background: '#fff6dc' },
  subtle: { fontSize: 12, color: theme.palette.text.secondary },
  drill: {
    cursor: 'pointer',
    borderBottom: '1px dotted currentColor',
    '&:hover': { color: theme.palette.primary.main }
  },
  addBar: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
    flexWrap: 'wrap',
    padding: theme.spacing(1.25),
    marginBottom: theme.spacing(1.5),
    background: '#f5f8fc',
    border: '1px solid #dbe5f0',
    borderRadius: 6
  },
  empty: { padding: theme.spacing(4), color: theme.palette.text.secondary }
}))

const inr = (v) =>
  v === null || v === undefined
    ? '—'
    : `₹${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`

// ------------------------------------------------------------------ dashboard

const Tile = ({ label, value, note, tone, classes }) => (
  <div className={classes.tile}>
    <div className={classes.tileKey}>{label}</div>
    <div className={[classes.tileVal, tone ? classes[tone] : ''].join(' ')}>
      {value}
    </div>
    {note ? <div className={classes.tileNote}>{note}</div> : null}
  </div>
)

// The cash handed to one teacher, line by line, as entered in the cash book.
const DispatchList = ({ teacher, label, onClose, classes }) => (
  <Dialog open fullWidth maxWidth="md" onClose={onClose}>
    <DialogTitle>
      Cash dispatched to {teacher.teacher} · {label}
    </DialogTitle>
    <DialogContent>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Date</TableCell>
            <TableCell>Centre</TableCell>
            <TableCell align="right">Amount</TableCell>
            <TableCell>Remark</TableCell>
            <TableCell>Photo</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {teacher.dispatches.map((d) => (
            <TableRow key={d.uuid}>
              <TableCell>
                {new Date(d.date).toLocaleDateString('en-IN', {
                  day: 'numeric',
                  month: 'short'
                })}
              </TableCell>
              <TableCell>{d.centre}</TableCell>
              <TableCell align="right">{inr(d.amount)}</TableCell>
              <TableCell>{d.note}</TableCell>
              <TableCell>
                {d.proof ? (
                  <a href={d.proof} target="_blank" rel="noopener noreferrer">
                    View
                  </a>
                ) : (
                  '–'
                )}
              </TableCell>
            </TableRow>
          ))}
          {!teacher.dispatches.length && (
            <TableRow>
              <TableCell
                colSpan={5}
                className={classes.empty}
                style={{ whiteSpace: 'normal' }}
              >
                No cash dispatch entered for this period. The centre adds it in
                Cash register › + Add entry.
              </TableCell>
            </TableRow>
          )}
          <TableRow>
            <TableCell colSpan={2}>
              <b>Total · {teacher.dispatches.length} dispatches</b>
            </TableCell>
            <TableCell align="right">
              <b>{inr(teacher.dispatched)}</b>
            </TableCell>
            <TableCell colSpan={2} />
          </TableRow>
        </TableBody>
      </Table>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Close</Button>
    </DialogActions>
  </Dialog>
)

// HQ's view of the dispatch sheets: what each teacher's students paid in the
// month (by date of receipt), the split, how the payout went, and the cash
// actually handed over. "April to date" adds the months up.
const PayoutTable = ({ month, ytd = false, classes }) => {
  const [data, setData] = useState(null)
  const [showModes, setShowModes] = useState(false)
  const [cumulative, setCumulative] = useState(ytd)
  const [listOf, setListOf] = useState(null)
  // The page's Period picking "April to date" sets it here too.
  useEffect(() => setCumulative(ytd), [ytd])
  useEffect(() => {
    let live = true
    setData(null)
    fetchAcadsDispatch(month, cumulative)
      .then((res) => live && setData(res))
      .catch(
        () => live && showToast('Could not load the payout', constants.ERROR)
      )
    return () => {
      live = false
    }
  }, [month, cumulative])
  if (!data) return <CircularProgress size={22} />
  const modes = showModes ? data.modes : []
  const money = (v) => (v === null || v === undefined ? '—' : inr(v))
  const cols = [
    ['revenue', 'Revenue'],
    ['teacher_share', "Teacher's share"],
    ['paid_to_teacher', 'Paid to teacher'],
    ['net_payable', 'Net payable'],
    ['bank', 'Bank'],
    ['cash', 'Cash'],
    ['dispatched', 'Cash dispatched'],
    ['cash_to_give', 'Cash still to give'],
    ['oh_share', 'OH share'],
    ['gst', 'GST on OH share']
  ]
  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          margin: '8px 0'
        }}
      >
        {[
          [false, 'This month'],
          [true, 'April to date']
        ].map(([value, label]) => (
          <Chip
            key={label}
            size="small"
            label={label}
            clickable
            color={cumulative === value ? 'primary' : 'default'}
            onClick={() => setCumulative(value)}
          />
        ))}
        <Typography variant="body2" className={classes.muted}>
          Fees received in {data.month_label}, by date of receipt. Bank and cash
          are what was saved on the Dispatch sheet; cash dispatched is from the
          cash book.
        </Typography>
        <Chip
          size="small"
          label="Show payment modes"
          clickable
          color={showModes ? 'primary' : 'default'}
          onClick={() => setShowModes(!showModes)}
        />
      </div>
      <div style={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Teacher</TableCell>
              {modes.map((m) => (
                <TableCell key={m} align="right">
                  {m}
                </TableCell>
              ))}
              {cols.map(([key, label]) => (
                <TableCell key={key} align="right">
                  {label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.teachers.map((t) => (
              <TableRow key={t.teacher_uuid}>
                <TableCell>{t.teacher}</TableCell>
                {modes.map((m) => (
                  <TableCell key={m} align="right">
                    {t.modes[m] ? inr(t.modes[m]) : '—'}
                  </TableCell>
                ))}
                {cols.map(([key]) => (
                  <TableCell
                    key={key}
                    align="right"
                    style={
                      key === 'net_payable' ? { fontWeight: 600 } : undefined
                    }
                    title={
                      (key === 'bank' ||
                        key === 'cash' ||
                        key === 'cash_to_give') &&
                      t.cash === null
                        ? 'Not saved yet: set it on the Dispatch sheet'
                        : undefined
                    }
                  >
                    {key === 'dispatched' ? (
                      <span
                        role="button"
                        tabIndex={0}
                        className={classes.drill}
                        title="See the dispatches"
                        onClick={() => setListOf(t)}
                        onKeyDown={(e) => e.key === 'Enter' && setListOf(t)}
                      >
                        {t.dispatched ? inr(t.dispatched) : '—'}
                      </span>
                    ) : (
                      money(t[key])
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
            {!data.teachers.length && (
              <TableRow>
                <TableCell colSpan={cols.length + 1} className={classes.empty}>
                  No fees received in {data.month_label}.
                </TableCell>
              </TableRow>
            )}
            {data.teachers.length > 0 && (
              <TableRow>
                <TableCell>
                  <b>Total</b>
                </TableCell>
                {modes.map((m) => (
                  <TableCell key={m} align="right">
                    <b>
                      {inr(
                        data.teachers.reduce(
                          (sum, t) => sum + (t.modes[m] || 0),
                          0
                        )
                      )}
                    </b>
                  </TableCell>
                ))}
                {cols.map(([key]) => (
                  <TableCell key={key} align="right">
                    <b>{inr(data.total[key])}</b>
                  </TableCell>
                ))}
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      {listOf && (
        <DispatchList
          teacher={listOf}
          label={data.month_label}
          onClose={() => setListOf(null)}
          classes={classes}
        />
      )}
    </div>
  )
}

const DashboardTab = ({ month, ytd = false, onOpen, classes }) => {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  // Revenue = what was paid against the period's fees; Receipt = what came in
  // during the period, whichever month it paid for.
  const [basis, setBasis] = useState('revenue')
  // Which breakdown is open at the bottom. Teacher wise first — it is the
  // worklist, and it is what somebody opening this page is here to act on.
  const [view, setView] = useState('teacher')
  // A figure in the teacher table opens the student-by-student list it is the
  // total of, for this month.
  // The breakup is one fee month on the revenue basis, so a figure only
  // opens it when that is what the figure is.
  const canDrill = onOpen && basis === 'revenue' && !ytd
  const drill = (teacher, kind, label) =>
    canDrill ? (
      <span
        role="button"
        tabIndex={0}
        className={classes.drill}
        title="See the students this figure is made of"
        onClick={() => onOpen(kind, teacher)}
        onKeyDown={(e) => e.key === 'Enter' && onOpen(kind, teacher)}
      >
        {label}
      </span>
    ) : (
      label
    )

  useEffect(() => {
    let live = true
    setLoading(true)
    fetchAcadsSummary(month, basis, ytd)
      .then((res) => live && setData(res))
      .catch(
        () => live && showToast('Could not load the dashboard', constants.ERROR)
      )
      .finally(() => live && setLoading(false))
    return () => {
      live = false
    }
  }, [month, basis, ytd])

  if (loading && !data) return <CircularProgress size={22} />
  if (!data) return null

  return (
    <div>
      {/* Four tiles, each answering one question and carrying its own detail
          line. Ten small tiles read as a wall of numbers; the eye picks none of
          them. */}
      <div className={classes.tiles}>
        <Tile
          label="Left to enter"
          value={`${data.remaining}`}
          tone={data.remaining ? 'warn' : 'good'}
          note={`in ${data.month_label} · ${data.teachers_done} of ${data.teachers_total} teachers done`}
          classes={classes}
        />
        <Tile
          label="Collected"
          value={inr(data.collected)}
          tone="good"
          note={`of ${inr(data.expected)} expected · ${
            data.paid
          } students paid`}
          classes={classes}
        />
        <Tile
          label="Outstanding"
          value={inr(data.pending)}
          tone={data.pending > 0 ? 'warn' : 'good'}
          note={data.ytd ? 'April to date' : 'this month'}
          classes={classes}
        />
        <Tile
          label="OH share"
          value={inr(data.split.oh)}
          note={`teacher ${inr(data.split.teacher)} · GST ${inr(
            data.split.gst
          )}`}
          classes={classes}
        />
      </div>
      {data.signoff && data.signoff.approved && (
        <Typography
          variant="body2"
          className={classes.muted}
          style={{ marginBottom: 12 }}
        >
          {`Signed off by ${
            data.signoff.approved_by || 'HQ Finance'
          } — this month is closed for edits.`}
        </Typography>
      )}

      {/* The breakdowns live under tabs rather than stacked, so the page ends
          with one table instead of three competing for the same attention.
          "By teacher" is first and is the worklist: ordered by who has the most
          left, finished teachers last, so the order is itself the instruction. */}
      <Tabs
        value={view}
        onChange={(e, v) => setView(v)}
        indicatorColor="primary"
        textColor="primary"
        className={classes.subTabs}
      >
        <Tab value="teacher" label="Teacher wise" />
        <Tab value="mode" label="By payment mode" />
        <Tab value="centre" label="By centre" />
        <Tab value="payout" label="Teacher payout" />
      </Tabs>

      {view === 'payout' && (
        <PayoutTable month={month} ytd={ytd} classes={classes} />
      )}

      {view === 'teacher' && (
        <>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              margin: '8px 0'
            }}
          >
            {[
              ['revenue', 'Revenue'],
              ['receipt', 'Receipt']
            ].map(([value, label]) => (
              <Chip
                key={value}
                size="small"
                label={label}
                clickable
                color={basis === value ? 'primary' : 'default'}
                onClick={() => setBasis(value)}
              />
            ))}
            <Typography variant="body2" className={classes.muted}>
              {basis === 'revenue'
                ? `Fees for ${data.period_label} and what was paid against them.`
                : `Fees for ${data.period_label}, and the money that came in during it — whichever month it paid for.`}
            </Typography>
          </div>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Teacher</TableCell>
                <TableCell align="right">Students</TableCell>
                <TableCell align="right">Expected</TableCell>
                <TableCell align="right">Collected</TableCell>
                <TableCell align="right">Pending</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.teachers.map((t) => (
                <TableRow key={t.teacher_uuid}>
                  <TableCell>{t.teacher}</TableCell>
                  <TableCell align="right">
                    {drill(t, 'students', t.students)}
                  </TableCell>
                  <TableCell align="right">
                    {drill(t, 'expected', inr(t.expected))}
                  </TableCell>
                  <TableCell align="right">
                    {drill(t, 'collected', inr(t.collected))}
                  </TableCell>
                  <TableCell align="right">
                    {drill(t, 'pending', inr(t.pending))}
                  </TableCell>
                </TableRow>
              ))}
              {!data.teachers.length && (
                <TableRow>
                  <TableCell colSpan={5} className={classes.empty}>
                    No students yet. Add them from Collect a fee.
                  </TableCell>
                </TableRow>
              )}
              {data.teachers.length > 0 && (
                <TableRow>
                  <TableCell>
                    <b>Total</b>
                  </TableCell>
                  <TableCell align="right">
                    <b>{data.students}</b>
                  </TableCell>
                  <TableCell align="right">
                    <b>{inr(data.expected)}</b>
                  </TableCell>
                  <TableCell align="right">
                    <b>{inr(data.collected)}</b>
                  </TableCell>
                  <TableCell align="right">
                    <b>{inr(data.pending)}</b>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </>
      )}

      {view === 'mode' && (
        <Table size="small">
          <TableBody>
            {Object.keys(data.by_mode)
              .filter((mode) => data.by_mode[mode])
              .map((mode) => (
                <TableRow key={mode}>
                  <TableCell>{mode}</TableCell>
                  <TableCell align="right">{inr(data.by_mode[mode])}</TableCell>
                </TableRow>
              ))}
            {!Object.keys(data.by_mode).some((m) => data.by_mode[m]) && (
              <TableRow>
                <TableCell className={classes.empty}>
                  Nothing collected yet.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}

      {view === 'centre' && (
        <Table size="small">
          <TableBody>
            <TableRow>
              <TableCell>Theatre Road</TableCell>
              <TableCell align="right">{inr(data.by_centre.TR)}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Salt Lake</TableCell>
              <TableCell align="right">{inr(data.by_centre.SL)}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      )}
    </div>
  )
}

// ------------------------------------------------------------ figure breakup

// What each dashboard figure is the total of. `only` picks the students that
// make the figure up; `column` is the one whose total equals it.
const FIGURES = {
  students: { label: 'Students', column: '', only: () => true },
  expected: { label: 'Expected', column: 'expected', only: () => true },
  collected: {
    label: 'Collected',
    column: 'collected',
    only: (r) => r.collected > 0
  },
  pending: {
    label: 'Pending',
    column: 'pending',
    only: (r) => r.pending !== 0
  },
  unpaid: { label: 'Not paid', column: '', only: (r) => !r.collected }
}

/**
 * One teacher's month, student by student — the list a dashboard figure is
 * the total of. It reads the same rows the dashboard adds up, so the total at
 * the bottom of the highlighted column is the number that was clicked.
 */
const TeacherBreakup = ({ month, teacher, figure, classes }) => {
  const [rows, setRows] = useState(null)
  const [label, setLabel] = useState('')
  const [all, setAll] = useState(false)

  useEffect(() => {
    setRows(null)
    fetchAcadsEntry(month, teacher.teacher_uuid)
      .then((data) => {
        setLabel(data.month_label)
        setRows(
          data.rows.map((r) => {
            const expected = Number(r.fees_due || 0)
            const collected = Number(r.amount_paid || 0)
            return {
              key: r.enrolment,
              student: r.student_name,
              about: [
                r.class_label && `Class ${r.class_label}`,
                r.centre,
                r.parent_contact
              ]
                .filter(Boolean)
                .join(' · '),
              expected,
              collected,
              pending: expected - collected,
              paid: [
                r.mode,
                r.date_paid &&
                  new Date(r.date_paid).toLocaleDateString('en-IN', {
                    day: 'numeric',
                    month: 'short'
                  })
              ]
                .filter(Boolean)
                .join(' · ')
            }
          })
        )
      })
      .catch(() => {
        setRows([])
        showToast('Could not load the students', constants.ERROR)
      })
  }, [month, teacher.teacher_uuid])

  if (!rows) return <CircularProgress size={24} />

  const what = FIGURES[figure] || FIGURES.expected
  const shown = all ? rows : rows.filter(what.only)
  const total = (key) => shown.reduce((sum, r) => sum + r[key], 0)
  const mark = (key) => (what.column === key ? classes.picked : undefined)
  const money = (key) => (
    <TableCell align="right" className={mark(key)}>
      <b>{inr(total(key))}</b>
    </TableCell>
  )

  return (
    <div>
      <Typography variant="subtitle1" style={{ marginBottom: 8 }}>
        <b>{teacher.teacher}</b> · {label} · <b>{what.label}</b>
        {what.column ? ` ${inr(total(what.column))}` : ''} · {shown.length}{' '}
        student{shown.length === 1 ? '' : 's'}
        {shown.length !== rows.length || all ? (
          <Chip
            size="small"
            clickable
            style={{ marginLeft: 12 }}
            color={all ? 'primary' : 'default'}
            label={
              all
                ? `Showing all ${rows.length} students`
                : `Show all ${rows.length} students`
            }
            onClick={() => setAll(!all)}
          />
        ) : null}
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Student</TableCell>
            <TableCell align="right" className={mark('expected')}>
              Expected
            </TableCell>
            <TableCell align="right" className={mark('collected')}>
              Collected
            </TableCell>
            <TableCell align="right" className={mark('pending')}>
              Pending
            </TableCell>
            <TableCell>Paid by</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {shown.map((r) => (
            <TableRow key={r.key}>
              <TableCell>
                <b>{r.student}</b>
                <div className={classes.subtle}>{r.about}</div>
              </TableCell>
              <TableCell align="right" className={mark('expected')}>
                {inr(r.expected)}
              </TableCell>
              <TableCell align="right" className={mark('collected')}>
                {r.collected ? inr(r.collected) : '—'}
              </TableCell>
              <TableCell align="right" className={mark('pending')}>
                {r.pending ? inr(r.pending) : '—'}
              </TableCell>
              <TableCell>{r.paid || (r.collected ? '' : 'Not paid')}</TableCell>
            </TableRow>
          ))}
          {!shown.length && (
            <TableRow>
              <TableCell colSpan={5} className={classes.empty}>
                No students in this figure.
              </TableCell>
            </TableRow>
          )}
          <TableRow>
            <TableCell>
              <b>Total · {shown.length} students</b>
            </TableCell>
            {money('expected')}
            {money('collected')}
            {money('pending')}
            <TableCell />
          </TableRow>
        </TableBody>
      </Table>
    </div>
  )
}

export { DashboardTab, TeacherBreakup, useStyles }
